"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { EditorialAccountNav } from "@/components/editorial-system/editorial-account-nav";
import {
	type EditorialNavLink,
	type EditorialPlaceNav,
	isNavLinkActive,
} from "@/components/editorial-system/editorial-nav-shared";
import { EditorialWordmark } from "@/components/editorial-system/editorial-wordmark";
import { cn } from "@/lib/utils";

interface EditorialPhoneMenuProps {
	primary: EditorialNavLink[];
	user: EditorialNavLink[];
	place?: EditorialPlaceNav;
}

/** Full-screen phone menu: primary nav in 15px caps, then personal links. */
export function EditorialPhoneMenu({
	primary,
	user,
	place,
}: EditorialPhoneMenuProps) {
	const [open, setOpen] = useState(false);
	const pathname = usePathname() ?? "/";
	const menuId = useId();
	const closeRef = useRef<HTMLButtonElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	// The menu opens over everything, so it renders at the top of the
	// editorial root rather than inside the fixed top band (whose stacking
	// context would keep it under the bottom band).
	const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
	useEffect(() => {
		setPortalTarget(
			triggerRef.current?.closest<HTMLElement>(".ed-root") ?? null,
		);
	}, []);

	// Close when the route changes.
	useEffect(() => {
		setOpen(false);
	}, [pathname]);

	useEffect(() => {
		if (!open) return;
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		closeRef.current?.focus();
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") setOpen(false);
		};
		window.addEventListener("keydown", onKey);
		return () => {
			document.body.style.overflow = previousOverflow;
			window.removeEventListener("keydown", onKey);
			triggerRef.current?.focus();
		};
	}, [open]);

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				className="ed-button-text ed-nav"
				aria-expanded={open}
				aria-controls={menuId}
				onClick={() => setOpen(true)}
				style={{ minHeight: 44 }}
			>
				Menu
			</button>
			{open && portalTarget
				? createPortal(
						<div
							id={menuId}
							role="dialog"
							aria-modal="true"
							aria-label="Menu"
							className="ed-menu"
						>
							<div
								className="ed-band__row"
								style={{ padding: "14px var(--ed-gutter)" }}
							>
								<EditorialWordmark />
								<button
									ref={closeRef}
									type="button"
									className="ed-button-text ed-nav"
									onClick={() => setOpen(false)}
									style={{ minHeight: 44 }}
								>
									Close
								</button>
							</div>
							<nav aria-label="Primary" className="ed-menu__nav">
								{primary.map((link) => {
									const active = isNavLinkActive(link, pathname);
									return (
										<div key={link.href}>
											<Link
												href={link.href}
												aria-current={active ? "page" : undefined}
												className={cn("ed-nav-lg", active && "ed-active")}
											>
												{link.label}
											</Link>
											{place && link.href === place.parentHref ? (
												<div style={{ paddingLeft: 16 }}>
													<Link href={place.href} className="ed-caption">
														{place.title}
													</Link>
													{place.links.map((sub) => {
														const subActive = isNavLinkActive(sub, pathname);
														return (
															<Link
																key={sub.href}
																href={sub.href}
																aria-current={subActive ? "page" : undefined}
																className={cn(
																	"ed-body",
																	subActive && "ed-active",
																)}
																style={{ paddingLeft: 16 }}
															>
																{sub.label}
															</Link>
														);
													})}
												</div>
											) : null}
										</div>
									);
								})}
								<span
									aria-hidden
									className="ed-ui"
									style={{ padding: "8px 0" }}
								>
									—
								</span>
								{user.some(
									(link) => link.icon || link.avatarUrl !== undefined,
								) ? (
									<EditorialAccountNav
										links={user}
										className="ed-account-nav--menu"
									/>
								) : (
									user.map((link) => (
										<Link
											key={link.href}
											href={link.href}
											className="ed-caption"
										>
											{link.label}
										</Link>
									))
								)}
							</nav>
							<span
								className="ed-meta ed-secondary"
								style={{ marginTop: "auto", padding: 20 }}
							>
								© {new Date().getFullYear()} COLORS
							</span>
						</div>,
						portalTarget,
					)
				: null}
		</>
	);
}
